import { HttpCoreService } from "@/services/HttpCoreService";
import type {
  ConnectorConfig,
  ConnectorListResponse,
  SaveConnectorRequest,
  SiteMappingPair,
  SiteMappingWorkspace,
  TestConnectorRequest,
  TestConnectorResponse,
} from "@/types/connector.type";

export interface IConnectorRepository {
  getConnectors(): Promise<ConnectorConfig[]>;
  getConnector(connectorType: string): Promise<ConnectorConfig | null>;
  /** Fetch the connector binding for a specific CTMS study UUID and connector type. Returns null when not found. */
  getConnectorByStudy(
    studyId: string,
    connectorType?: string,
  ): Promise<ConnectorConfig | null>;
  saveConnector(payload: SaveConnectorRequest): Promise<ConnectorConfig>;
  /** Update an existing connector binding for a CTMS study UUID. */
  updateConnector(
    studyId: string,
    payload: SaveConnectorRequest,
  ): Promise<ConnectorConfig>;
  testConnector(payload: TestConnectorRequest): Promise<TestConnectorResponse>;
  /** Delete the connector binding for a CTMS study UUID and connector type. */
  deleteConnector(studyId: string, connectorType?: string): Promise<void>;
  getSiteMappingWorkspace(
    studyId: string,
    connectorType: string,
  ): Promise<SiteMappingWorkspace>;
  saveSiteMappings(
    studyId: string,
    connectorType: string,
    mappings: SiteMappingPair[],
  ): Promise<SiteMappingWorkspace>;
  deleteSiteMapping(
    studyId: string,
    connectorType: string,
    mappingId: string,
  ): Promise<SiteMappingWorkspace>;
}

export class ConnectorRepository implements IConnectorRepository {
  private readonly http: HttpCoreService;

  constructor(http: HttpCoreService) {
    this.http = http;
  }

  async getConnectors(): Promise<ConnectorConfig[]> {
    try {
      const response =
        await this.http.get<ConnectorListResponse>("/connectors");
      if (
        response.statusCode >= 200 &&
        response.statusCode < 300 &&
        response.data
      ) {
        return response.data.items ?? [];
      }
      return [];
    } catch {
      return [];
    }
  }

  async getConnector(connectorType: string): Promise<ConnectorConfig | null> {
    try {
      const response = await this.http.get<ConnectorConfig>(
        `/connectors/${connectorType}`,
      );
      if (
        response.statusCode >= 200 &&
        response.statusCode < 300 &&
        response.data
      ) {
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  async getConnectorByStudy(
    studyId: string,
    connectorType = "EDC",
  ): Promise<ConnectorConfig | null> {
    try {
      const response = await this.http.get<ConnectorConfig>(
        `/connectors/study/${studyId}?connectorType=${connectorType}`,
      );
      if (
        response.statusCode >= 200 &&
        response.statusCode < 300 &&
        response.data
      ) {
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  }

  async saveConnector(payload: SaveConnectorRequest): Promise<ConnectorConfig> {
    const response = await this.http.post<ConnectorConfig>(
      "/connectors",
      payload,
    );
    if (
      response.statusCode >= 200 &&
      response.statusCode < 300 &&
      response.data
    ) {
      return response.data;
    }
    throw new Error(
      response.message ?? "Failed to save connector configuration.",
    );
  }

  async updateConnector(
    studyId: string,
    payload: SaveConnectorRequest,
  ): Promise<ConnectorConfig> {
    const response = await this.http.patch<ConnectorConfig>(
      `/connectors/study/${studyId}`,
      payload,
    );
    if (
      response.statusCode >= 200 &&
      response.statusCode < 300 &&
      response.data
    ) {
      return response.data;
    }
    throw new Error(
      response.message ?? "Failed to update connector configuration.",
    );
  }

  async testConnector(
    payload: TestConnectorRequest,
  ): Promise<TestConnectorResponse> {
    try {
      const response = await this.http.post<TestConnectorResponse>(
        "/connectors/test",
        payload,
      );
      if (
        response.statusCode >= 200 &&
        response.statusCode < 300 &&
        response.data
      ) {
        return response.data;
      }
      return {
        success: false,
        message: response.message ?? "Connection test failed.",
      };
    } catch {
      return {
        success: false,
        message: "Connection test failed. Unable to reach the server.",
      };
    }
  }

  async deleteConnector(studyId: string, connectorType = "EDC"): Promise<void> {
    await this.http.delete(
      `/connectors/study/${studyId}?connectorType=${connectorType}`,
    );
  }

  async getSiteMappingWorkspace(
    studyId: string,
    connectorType: string,
  ): Promise<SiteMappingWorkspace> {
    const response = await this.http.get<SiteMappingWorkspace>(
      `/connectors/study/${studyId}/site-mapping?connectorType=${connectorType}`,
    );
    return this.unwrapWorkspace(response, "Failed to load site mapping.");
  }

  async saveSiteMappings(
    studyId: string,
    connectorType: string,
    mappings: SiteMappingPair[],
  ): Promise<SiteMappingWorkspace> {
    const response = await this.http.put<
      SiteMappingWorkspace,
      { connectorType: string; mappings: SiteMappingPair[] }
    >(`/connectors/study/${studyId}/site-mapping`, {
      connectorType,
      mappings,
    });
    return this.unwrapWorkspace(response, "Failed to save site matches.");
  }

  async deleteSiteMapping(
    studyId: string,
    connectorType: string,
    mappingId: string,
  ): Promise<SiteMappingWorkspace> {
    const response = await this.http.delete<SiteMappingWorkspace>(
      `/connectors/study/${studyId}/site-mapping/${mappingId}?connectorType=${connectorType}`,
    );
    return this.unwrapWorkspace(response, "Failed to remove site match.");
  }

  private unwrapWorkspace(
    response: {
      statusCode: number;
      data?: SiteMappingWorkspace | null;
      message?: string;
    },
    fallbackMessage: string,
  ): SiteMappingWorkspace {
    if (
      response.statusCode >= 200 &&
      response.statusCode < 300 &&
      response.data
    ) {
      return response.data;
    }
    throw new Error(response.message ?? fallbackMessage);
  }
}
